/**
 * Ayarlar › API kullanımı (v0.1.164, specs/api-kullanimi.md): GET /settings/api-usage. Every outside API Twin uses on
 * one page: today, this month, what is left where the provider says so (when it was read and from where), the money
 * where it costs money, and the last 30 days day by day. A provider that does not report what is left has no quota
 * row; the page says so instead of guessing.
 */
import { z } from 'zod';
import { countSchema, isoDateSchema, isoDateTimeSchema, microUsdSchema } from '../ids.js';
/** Page order. The worker's meter uses the same ids (twin.api_usage_daily.provider). */
export const API_USAGE_PROVIDERS = [
    'genesis',
    'anthropic',
    'gemini',
    'brave',
    'serper',
    'millionverifier',
    'reoon',
    'etsy',
    'telegram',
    'gmail',
    'portal',
    'ga4',
    // v0.1.166: the site statistics readers (specs/site-istatistikleri.md).
    'search_console',
    'bing_webmaster',
    'higgsfield',
    'here',
    'places',
    'overpass',
    'abn',
    'nzbn',
];
/**
 * Grounded Gemini prompts the lead scan may make in a day (v0.1.162): Twin's own cap, not Google's. The worker's
 * GEMINI_FREE_CALLS_PER_DAY is this value; the page shows today's scan prompts against it.
 */
// 28 Oct 2026 (Genesis cost review, approved): new scanning is no longer a goal, so 15 a day (was 40).
export const GEMINI_TWIN_DAILY_CAP = 15;
/**
 * Brave searches of the month kept for the weekly AI visibility check (v0.1.164): when the latest monthly Brave reading
 * (twin.api_quota_state, from Brave's response headers) says fewer than this are left, the lead scan and the lead
 * verification leave Brave alone and the visibility check still uses it. An unknown remainder changes nothing.
 */
export const BRAVE_VISIBILITY_RESERVE = 40;
/** connected: a key is entered or calls went through in the last 7 days. off: switched off in code. */
export const API_USAGE_STATUSES = ['connected', 'key_missing', 'off', 'not_in_twin'];
export const API_USAGE_QUOTA_WINDOWS = ['day', 'month', 'total', '5h', 'week'];
export const API_USAGE_QUOTA_UNITS = ['request', 'credit', 'usd', 'percent'];
/**
 * Where a quota came from: a response header, a balance or usage endpoint, entered by hand, the Claude rate limit
 * events of the GM runs, or Twin's own cap (the team month cap, the Gemini daily cap).
 */
export const API_USAGE_QUOTA_SOURCES = [
    'response_header',
    'balance_api',
    'usage_api',
    'manual',
    'rate_limit_event',
    'twin_cap',
];
/**
 * near: 90 % or more of the limit used; over: nothing left; unknown: neither the limit nor what is left is known, or
 * the window has reset since the reading.
 */
export const API_USAGE_QUOTA_LEVELS = ['ok', 'near', 'over', 'unknown'];
/**
 * How a provider tells what is left: with each answer (Brave and Etsy headers, Claude's rate limit event), from a
 * balance endpoint (MillionVerifier, Reoon), from a usage endpoint (Genesis), or never. A provider that reports but
 * has no reading yet is "not read yet", not "does not report".
 */
export const API_USAGE_QUOTA_REPORTED = ['never', 'header', 'balance', 'usage'];
const quotaNumberSchema = z.number().nonnegative().nullable();
export const apiUsageCountSchema = z.strictObject({
    calls: countSchema,
    errors: countSchema,
});
export const apiUsageDaySchema = z.strictObject({
    day: isoDateSchema,
    calls: countSchema,
    errors: countSchema,
    /** Real money of the day in the cost ledger. */
    microUsd: microUsdSchema,
});
export const apiUsageQuotaSchema = z.strictObject({
    window: z.enum(API_USAGE_QUOTA_WINDOWS),
    unit: z.enum(API_USAGE_QUOTA_UNITS),
    limit: quotaNumberSchema,
    remaining: quotaNumberSchema,
    used: quotaNumberSchema,
    resetsAt: isoDateTimeSchema.nullable(),
    /** When it was read (for a Twin cap: when the page was built). */
    capturedAt: isoDateTimeSchema,
    source: z.enum(API_USAGE_QUOTA_SOURCES),
    detailTr: z.string().max(200).nullable(),
    level: z.enum(API_USAGE_QUOTA_LEVELS),
    /** The window reset after this reading (resetsAt has passed): the figure is the last one, not the current one. */
    expired: z.boolean().optional(),
});
export const apiUsageProviderSchema = z.strictObject({
    id: z.enum(API_USAGE_PROVIDERS),
    nameTr: z.string(),
    purposeTr: z.string(),
    /** What one count is ("istek", "arama", "mesaj", "çalıştırma"). */
    unitTr: z.string(),
    status: z.enum(API_USAGE_STATUSES),
    quotaReported: z.enum(API_USAGE_QUOTA_REPORTED),
    today: apiUsageCountSchema,
    month: apiUsageCountSchema,
    /** Real money in the cost ledger; null when Twin records no amount for this provider. */
    todayMicroUsd: microUsdSchema.nullable(),
    monthMicroUsd: microUsdSchema.nullable(),
    /** The Claude subscription priced at API rates: shown apart, never billed (anthropic only). */
    notionalMonthMicroUsd: microUsdSchema.nullable(),
    /** One entry per day of the view's days, oldest first. */
    daily: z.array(apiUsageDaySchema),
    quotas: z.array(apiUsageQuotaSchema),
    lastCallAt: isoDateTimeSchema.nullable(),
    /** The newest error kind of the 30 days ("timeout", "http_429"); never a message. */
    lastError: z.strictObject({ kind: z.string().regex(/^[a-z0-9_]{1,200}$/), at: isoDateTimeSchema }).nullable(),
    noteTr: z.string().nullable(),
    /** Where to look outside Twin (Higgsfield's own panel). */
    linkUrl: z
        .string()
        .regex(/^https:\/\/[^\s]{1,200}$/)
        .nullable(),
});
export const apiUsageSummarySchema = z.strictObject({
    realTodayMicroUsd: microUsdSchema,
    realMonthMicroUsd: microUsdSchema,
    notionalMonthMicroUsd: microUsdSchema,
    /**
     * Providers at 90 % or more of a limit. Credit pools of one provider are alternatives (Reoon: the free daily checks,
     * then the bought ones): such a provider is near or over only when every pool is.
     */
    nearLimit: countSchema,
    /** Providers with nothing left (for credit pools: in every pool). A window that has reset since is not counted. */
    overLimit: countSchema,
    /** Connected providers that never report what is left (quotaReported never). */
    noQuota: countSchema,
});
export const apiUsageViewSchema = z.strictObject({
    generatedAt: isoDateTimeSchema,
    /** UTC days, like the budget windows. */
    today: isoDateSchema,
    monthStart: isoDateSchema,
    days: z.array(isoDateSchema),
    summary: apiUsageSummarySchema,
    providers: z.array(apiUsageProviderSchema),
});
//# sourceMappingURL=api-usage.js.map